import { FC } from 'react';

import { BaseCheckableElement, HighlighterProps } from './CheckableElement';
import DivBasedHighlighter from '../components/Highlighter/DivBased';
import { GrammarCheckAlert } from '../types';
import { getScrollParent } from '../utils/dom';

export class ContentEditableCheckableElement extends BaseCheckableElement {
  private currentText = '';

  calculateAlertClientRects(alerts: GrammarCheckAlert[]): DOMRect[][] {
    this.currentText = this.getText();
    return alerts.map((alert) => this.calculateAlertClientRect(alert));
  }

  private calculateAlertClientRect(alert: GrammarCheckAlert): DOMRect[] {
    const { absOffset, length } = alert;
    if (!this.currentText || this.currentText.length < absOffset + length)
      return [];

    const nodeInfo = this.findTextNodeAtOffset(this.element, absOffset, length);
    if (!nodeInfo) return [];

    const { node, start, end } = nodeInfo;
    const range = document.createRange();
    range.setStart(node, start);
    range.setEnd(node, end);
    return Array.from(range.getClientRects());
  }

  private findTextNodeAtOffset(
    root: Node,
    absOffset: number,
    length: number,
  ): { node: Text; start: number; end: number } | null {
    let offset = 0;
    const walker = document.createTreeWalker(root, NodeFilter.SHOW_TEXT, null);
    let node: Text | null = null;
    while ((node = walker.nextNode() as Text | null)) {
      const nodeLength = node.textContent?.length ?? 0;
      if (absOffset < offset + nodeLength) {
        const start = Math.max(0, absOffset - offset);
        const end = Math.min(nodeLength, start + length);
        if (start >= end) return null;
        return { node, start, end };
      }
      offset += nodeLength;
    }
    return null;
  }

  insertText(text: string): void {
    this.element.innerText = text;
  }

  private updateAlertNode(alert: GrammarCheckAlert): void {
    const { absOffset, length, replacements } = alert;
    const nodeInfo = this.findTextNodeAtOffset(this.element, absOffset, length);
    if (!nodeInfo) return;
    const { node, start, end } = nodeInfo;

    const text = node.textContent ?? '';
    const newText = text.slice(0, start) + replacements[0] + text.slice(end);

    node.textContent = newText;
  }

  private dispatchInputEvent(): void {
    const inputEvent = new Event('input', { bubbles: true });
    this.element.dispatchEvent(inputEvent);
  }

  async replaceText(alert: GrammarCheckAlert): Promise<void> {
    this.updateAlertNode(alert);
    this.dispatchInputEvent();
  }

  async replaceAllText(alerts: GrammarCheckAlert[]): Promise<void> {
    const adjustedAlerts = this.adjustAlerts(alerts);
    adjustedAlerts.forEach((alert) => this.updateAlertNode(alert));
    this.dispatchInputEvent();
  }

  getText(): string {
    const clone = this.element.cloneNode(true) as HTMLElement;
    const quote = clone.querySelector('.gmail_quote');
    if (quote) quote.remove();
    return clone.textContent || '';
  }

  getHighlighter(): FC<HighlighterProps> {
    return DivBasedHighlighter;
  }

  getHighlighterContainer(): HTMLElement | null {
    const scrollParent = getScrollParent(this.element);
    const container =
      scrollParent === document.body ? this.element : scrollParent;
    if (container) {
      this.makeElementPositioned(container);
    }
    return container;
  }

  makeElementPositioned(element: HTMLElement | null): HTMLElement | null {
    if (!element) return null;
    const computedStyle = window.getComputedStyle(element);
    if (computedStyle.position === 'static') {
      element.style.position = 'relative';
    }
    return element;
  }
}
